import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatCard, SectionCard, Loader } from "@/components/PageUI";
import { EmptyState, Button } from "@/components/PageUI";
import {
  Users, UserRound, CalendarCheck, DollarSign, UserPlus, Swords, Sparkles, ArrowUpRight, PenSquare,
} from "lucide-react";

export default function Dashboard() {
  const { club } = useAuth();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  if (!data) return <Loader />;
  const cur = club?.currency || "USD";
  const money = (v) => `${cur === "USD" ? "$" : ""}${(v || 0).toLocaleString()} ${cur !== "USD" ? cur : ""}`.trim();

  return (
    <div className="animate-fade-up">
      <div className="mb-7">
        <p className="uppercase tracking-[0.2em] text-xs text-secondary font-semibold">Welcome to {club?.name}</p>
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight mt-1">Team Overview</h1>
      </div>

      <div className="mb-4 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-secondary/10 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="dashboard-write-blog-card">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl brand-gradient text-white flex items-center justify-center shrink-0"><PenSquare size={20} /></div>
          <div>
            <p className="font-heading font-semibold">Write today's blog</p>
            <p className="text-sm text-muted-foreground mt-0.5">Publish a fresh post to climb Google and bring your club new members — write your own or draft one with AI.</p>
          </div>
        </div>
        <Button className="shrink-0" onClick={() => navigate("/app/blog?new=1")} data-testid="dashboard-write-blog-btn">
          <PenSquare size={16} /> Write today's blog
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Teams" value={data.teams ?? 0} testid="stat-teams" />
        <StatCard icon={UserRound} label="Athletes" value={data.players ?? 0} accent="text-secondary" testid="stat-players" />
        <StatCard icon={CalendarCheck} label="Attendance" value={`${data.attendance_rate ?? 0}%`} accent="text-secondary" testid="stat-attendance" />
        <StatCard icon={DollarSign} label="Collected" value={money(data.revenue)} testid="stat-revenue" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <SectionCard title="Payments Due" className="lg:col-span-1" testid="card-payments-due"
          action={<Button size="sm" variant="ghost" onClick={() => navigate("/app/payments")}>Manage</Button>}>
          <p className="font-heading font-extrabold text-3xl">{money(data.fees_due_total)}</p>
          <p className="text-sm text-muted-foreground mt-1">{data.fees_due_count || 0} outstanding fees</p>
        </SectionCard>

        <SectionCard title="New Registrations" className="lg:col-span-1" testid="card-registrations"
          action={<Button size="sm" variant="ghost" onClick={() => navigate("/app/registrations")}>Review</Button>}>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl brand-gradient flex items-center justify-center">
              <UserPlus size={22} className="text-white" />
            </div>
            <div>
              <p className="font-heading font-extrabold text-3xl leading-none">{data.new_registrations || 0}</p>
              <p className="text-sm text-muted-foreground">pending approval</p>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="AI Coach" className="lg:col-span-1" testid="card-ai">
          <p className="text-sm text-muted-foreground">Generate practice plans, evaluations and season reports instantly.</p>
          <Button size="sm" className="mt-4 brand-gradient text-white border-0 hover:opacity-90" onClick={() => navigate("/app/ai")} data-testid="dashboard-ai-btn">
            <Sparkles size={16} className="mr-1.5" /> Open AI Coach
          </Button>
        </SectionCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <SectionCard title="Upcoming Events" testid="card-upcoming"
          action={<Button size="sm" variant="ghost" onClick={() => navigate("/app/matches")}>Calendar</Button>}>
          {data.upcoming_events?.length ? (
            <div className="space-y-3">
              {data.upcoming_events.map((m) => (
                <div key={m.id} className="flex items-center justify-between border-b border-border pb-3 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-accent flex items-center justify-center"><Swords size={16} className="text-primary" /></div>
                    <div>
                      <p className="font-medium text-sm">vs {m.opponent}</p>
                      <p className="text-xs text-muted-foreground">{m.location || "TBD"}</p>
                    </div>
                  </div>
                  <span className="text-sm text-muted-foreground">{m.date || "—"}</span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={Swords} title="No upcoming events" desc="Schedule a match to see it here." testid="empty-upcoming" />
          )}
        </SectionCard>

        <SectionCard title="Recent Messages" testid="card-messages"
          action={<Button size="sm" variant="ghost" onClick={() => navigate("/app/messages")}>Open <ArrowUpRight size={14} className="ml-1" /></Button>}>
          {data.recent_messages?.length ? (
            <div className="space-y-3">
              {data.recent_messages.map((m) => (
                <div key={m.id} className="border-b border-border pb-3 last:border-0">
                  <p className="text-sm"><span className="font-semibold">{m.author}:</span> {m.text}</p>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="No messages yet" desc="Start a team conversation." testid="empty-messages" />
          )}
        </SectionCard>
      </div>
    </div>
  );
}
